import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { getDatabase, ref, onValue, off, set, get } from 'firebase/database';

// Define hero networth data type
export interface HeroNetworth {
  id: number;
  name: string;
  imageUrl: string;
  networth: number[];
  kda: string;
}

interface TimeContextType {
  currentTimePoint: number;
  setCurrentTimePoint: (time: number) => void;
  gameDuration: number;
  setGameDuration: (duration: number) => void;
  isPlaying: boolean;
  setIsPlaying: (playing: boolean) => void;
  gameObjectives: Array<{
    time: number;
    type: string;
    team: string;
  }>;
  setGameObjectives: (objectives: Array<{time: number, type: string, team: string}>) => void;
  radiantHeroes: HeroNetworth[];
  direHeroes: HeroNetworth[];
  setRadiantHeroes: (heroes: HeroNetworth[]) => void;
  setDireHeroes: (heroes: HeroNetworth[]) => void;
  sessionId?: string;
}

const TimeContext = createContext<TimeContextType | undefined>(undefined);

interface TimeProviderProps {
  children: React.ReactNode;
  sessionId?: string;
}

export const TimeProvider: React.FC<TimeProviderProps> = ({ children, sessionId }) => {
  const [currentTimePoint, setCurrentTimePoint] = useState<number>(0);
  const [gameDuration, setGameDuration] = useState<number>(2388); // Default 39:48 in seconds
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [gameObjectives, setGameObjectives] = useState<Array<{time: number, type: string, team: string}>>([
    { time: 875, type: 'TOWER_KILL', team: 'radiant' },
    { time: 1024, type: 'TOWER_KILL', team: 'dire' },
    { time: 1320, type: 'ROSHAN_KILL', team: 'radiant' },
    { time: 1645, type: 'TOWER_KILL', team: 'radiant' },
    { time: 1735, type: 'TOWER_KILL', team: 'radiant' },
    { time: 1955, type: 'TOWER_KILL', team: 'dire' },
    { time: 2125, type: 'ROSHAN_KILL', team: 'dire' },
    { time: 2255, type: 'TOWER_KILL', team: 'radiant' },
    { time: 2315, type: 'TOWER_KILL', team: 'dire' },
    { time: 2380, type: 'ROSHAN_KILL', team: 'radiant' }
  ]);
  
  // Sample networth data over time for heroes
  const [radiantHeroes, setRadiantHeroes] = useState<HeroNetworth[]>([
    { 
      id: 1, 
      name: 'Gyrocopter', 
      imageUrl: 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/heroes/gyrocopter_full.png',
      networth: [600, 1200, 2400, 4300, 6713, 8100, 10200, 12400, 15300, 18200, 20864],
      kda: '3/6/2'
    },
    { 
      id: 2, 
      name: 'Monkey King', 
      imageUrl: 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/heroes/monkey_king_full.png',
      networth: [600, 1400, 2800, 4500, 6715, 9000, 11300, 14200, 16400, 17800, 18652],
      kda: '3/5/5'
    },
    { 
      id: 3, 
      name: 'Centaur Warrunner', 
      imageUrl: 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/heroes/centaur_full.png',
      networth: [600, 1100, 2300, 3900, 5780, 7300, 9100, 10700, 12600, 14200, 15088],
      kda: '1/5/5'
    },
    { 
      id: 4, 
      name: 'Warlock', 
      imageUrl: 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/heroes/warlock_full.png',
      networth: [600, 900, 1800, 2500, 2909, 4500, 6200, 7400, 8800, 10100, 11366],
      kda: '2/4/5'
    },
    { 
      id: 5, 
      name: 'Grimstroke', 
      imageUrl: 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/heroes/grimstroke_full.png',
      networth: [600, 950, 1700, 2600, 3524, 4700, 6100, 7300, 8900, 10200, 11263],
      kda: '1/6/6'
    }
  ]);
  
  const [direHeroes, setDireHeroes] = useState<HeroNetworth[]>([
    { 
      id: 6, 
      name: 'Templar Assassin', 
      imageUrl: 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/heroes/templar_assassin_full.png',
      networth: [600, 1300, 2900, 5000, 8104, 11200, 15400, 19300, 24700, 29100, 33647],
      kda: '14/0/4'
    },
    { 
      id: 7, 
      name: 'Storm Spirit', 
      imageUrl: 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/heroes/storm_spirit_full.png',
      networth: [600, 1200, 2500, 4200, 6051, 8200, 11300, 14500, 17800, 20500, 23362],
      kda: '7/1/16'
    },
    { 
      id: 8, 
      name: 'Bristleback', 
      imageUrl: 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/heroes/bristleback_full.png',
      networth: [600, 1300, 2700, 4400, 6415, 8700, 12100, 15300, 18200, 20400, 22725],
      kda: '2/2/22'
    },
    { 
      id: 9, 
      name: 'Slark', 
      imageUrl: 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/heroes/slark_full.png',
      networth: [600, 1100, 2000, 3100, 2837, 5100, 6800, 8200, 9700, 11300, 12981],
      kda: '1/4/11'
    },
    { 
      id: 10, 
      name: 'Jakiro', 
      imageUrl: 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/heroes/jakiro_full.png',
      networth: [600, 950, 1600, 2300, 3204, 4500, 6300, 7700, 9100, 10500, 11837],
      kda: '2/5/15'
    }
  ]);

  // Track if we're receiving changes from Firebase
  const isReceivingUpdate = useRef<boolean>(false);
  const lastUpdateTime = useRef<number>(Date.now());
  const initialDataCheckedRef = useRef<boolean>(false);

  // Firebase sync for timeline data
  useEffect(() => {
    if (!sessionId) return;
    
    console.log(`Setting up Firebase timeline sync for session: ${sessionId}`);
    
    const db = getDatabase();
    const timelineRef = ref(db, `sessions/${sessionId}/timeline`);
    
    // Reset the initialization flag when session ID changes
    initialDataCheckedRef.current = false;
    
    // First, check if timeline data exists
    get(timelineRef).then((snapshot) => {
      if (snapshot.exists()) {
        // Data exists, apply it immediately
        const timelineData = snapshot.val();
        console.log('Initial timeline data from Firebase:', timelineData);
        
        if (timelineData.currentTimePoint !== undefined) {
          setCurrentTimePoint(timelineData.currentTimePoint);
        }
        
        if (timelineData.isPlaying !== undefined) {
          setIsPlaying(timelineData.isPlaying);
        }
      } else {
        // No data exists yet, initialize with current state
        console.log('No timeline data exists yet, initializing...');
        const initialData = {
          currentTimePoint,
          isPlaying,
          lastUpdateTimestamp: Date.now()
        };
        
        set(timelineRef, initialData)
          .then(() => console.log('Timeline data initialized'))
          .catch(err => console.error('Error initializing timeline data:', err));
      }
      
      // Mark that we've checked and initialized if needed
      initialDataCheckedRef.current = true;
    }).catch((error) => {
      console.error('Error checking timeline data:', error);
      // Still mark as checked even if there was an error
      initialDataCheckedRef.current = true;
    });
    
    // Set up listener for future changes
    const unsubscribe = onValue(timelineRef, (snapshot) => {
      // Skip the first event if we just initialized
      if (!initialDataCheckedRef.current) {
        initialDataCheckedRef.current = true;
        return;
      }
      
      if (!snapshot.exists()) {
        return;
      }
      
      const timelineData = snapshot.val();
      console.log('Received timeline update:', timelineData);
      
      // Prevent loops by checking if we just sent this update
      if (Date.now() - lastUpdateTime.current < 1000) {
        console.log('Ignoring our own update');
        return;
      }
      
      // Apply changes from Firebase
      isReceivingUpdate.current = true;
      
      if (timelineData.currentTimePoint !== undefined) {
        setCurrentTimePoint(timelineData.currentTimePoint);
      }
      
      if (timelineData.isPlaying !== undefined) {
        setIsPlaying(timelineData.isPlaying);
      }
      
      isReceivingUpdate.current = false;
    });
    
    return () => {
      console.log(`Cleaning up Firebase timeline sync for session: ${sessionId}`);
      unsubscribe();
    };
  }, [sessionId]);

  // Sync local timeline changes to Firebase
  useEffect(() => {
    if (!sessionId || !initialDataCheckedRef.current) return;
    
    // Don't sync if we're receiving an update
    if (isReceivingUpdate.current) return;
    
    // Debounce updates to reduce Firebase writes
    const updateFirebase = setTimeout(() => {
      console.log('Syncing timeline to Firebase');
      
      const db = getDatabase();
      const timelineRef = ref(db, `sessions/${sessionId}/timeline`);
      
      const timelineData = {
        currentTimePoint,
        isPlaying,
        lastUpdateTimestamp: Date.now()
      };
      
      set(timelineRef, timelineData)
        .then(() => {
          console.log('Timeline sync successful');
          lastUpdateTime.current = Date.now();
        })
        .catch(err => console.error('Error syncing timeline:', err));
    }, 300);
    
    return () => clearTimeout(updateFirebase);
  }, [currentTimePoint, isPlaying, sessionId]);

  // Playback logic
  useEffect(() => {
    let intervalId: number | null = null;
    
    if (isPlaying) {
      intervalId = window.setInterval(() => {
        setCurrentTimePoint(prevTime => {
          if (prevTime >= gameDuration) {
            setIsPlaying(false);
            return gameDuration;
          }
          return prevTime + 1;
        });
      }, 100); // 10x speed (1 game second = 100ms)
    }
    
    return () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
    };
  }, [isPlaying, gameDuration]);

  return (
    <TimeContext.Provider value={{ 
      currentTimePoint, 
      setCurrentTimePoint, 
      gameDuration, 
      setGameDuration,
      isPlaying,
      setIsPlaying,
      gameObjectives,
      setGameObjectives,
      radiantHeroes,
      direHeroes,
      setRadiantHeroes,
      setDireHeroes,
      sessionId
    }}>
      {children}
    </TimeContext.Provider>
  );
};

export const useTimeContext = (): TimeContextType => {
  const context = useContext(TimeContext);
  if (!context) {
    throw new Error('useTimeContext must be used within a TimeProvider');
  }
  return context;
}; 